import React from "react";
import {
  Paper,
  Group,
  Image,
  Box,
  Stack,
  Text,
  Badge,
  Button,
  NumberInput,
} from "@mantine/core";
import { IconPackage } from "@tabler/icons-react";
import { Product } from "../../types/products";

interface ProductDetailCardProps {
  product: Product;
  quantity: number;
  onQuantityChange: (value: number) => void;
  showQuantityControls?: boolean;
  maxQuantity?: number;
  error?: string;
}

export default function ProductDetailCard({
  product,
  quantity,
  onQuantityChange,
  showQuantityControls = true,
  maxQuantity,
  error,
}: ProductDetailCardProps) {
  const effectiveMaxQuantity = maxQuantity ?? product.quantity;

  const incrementQuantity = () => {
    if (quantity < effectiveMaxQuantity) {
      onQuantityChange(quantity + 1);
    }
  };

  const decrementQuantity = () => {
    if (quantity > 1) {
      onQuantityChange(quantity - 1);
    }
  };

  const handleNumberInputChange = (value: number | string) => {
    const numValue = typeof value === "string" ? parseInt(value, 10) : value;
    if (!isNaN(numValue) && numValue >= 1 && numValue <= effectiveMaxQuantity) {
      onQuantityChange(numValue);
    }
  };

  return (
    <Paper p="md" withBorder bg="gray.0">
      <Group gap="md" align="flex-start">
        {/* Product Image */}
        {product.img_url ? (
          <Image
            src={product.img_url}
            w={60}
            h={60}
            fit="contain"
            alt={product.name}
          />
        ) : (
          <Box
            w={60}
            h={60}
            bg="gray.2"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "4px",
            }}
          >
            <IconPackage size={30} color="gray.6" />
          </Box>
        )}

        {/* Product Details */}
        <Stack gap="xs" style={{ flex: 1 }}>
          <Text fw={500} size="lg">
            {product.name}
          </Text>
          <Group gap="xs">
            <Badge variant="light">{product.category}</Badge>
            {product.company && (
              <Badge variant="light" color="blue">
                {product.company}
              </Badge>
            )}
          </Group>
          {product.description && (
            <Text size="sm" c="dimmed">
              {product.description}
            </Text>
          )}
          <Text size="sm" c="dimmed">
            ID: {product.custom_id}
          </Text>
        </Stack>

        {/* Price and Stock */}
        <Stack gap="xs" align="flex-end">
          <Text size="lg" fw={500}>
            ${product.list_price.toFixed(2)}
          </Text>
          <Text size="sm" c="dimmed">
            Stock: {product.quantity}
          </Text>

          {/* Quantity Controls */}
          {showQuantityControls && (
            <Group gap="xs" mt="xs">
              <Stack gap={4}>
                <Group gap="sm" align="center">
                  <Button
                    variant="outline"
                    size="xs"
                    onClick={decrementQuantity}
                    disabled={quantity <= 1}
                  >
                    -
                  </Button>

                  <NumberInput
                    value={quantity}
                    onChange={handleNumberInputChange}
                    min={1}
                    max={effectiveMaxQuantity}
                    hideControls
                    error={error}
                    styles={{
                      input: {
                        textAlign: "center",
                        width: "50px",
                      },
                    }}
                  />

                  <Button
                    variant="outline"
                    size="xs"
                    onClick={incrementQuantity}
                    disabled={quantity >= effectiveMaxQuantity}
                  >
                    +
                  </Button>
                </Group>
              </Stack>
            </Group>
          )}
        </Stack>
      </Group>
    </Paper>
  );
}
